import { useLayoutEffect, useState } from "react";
import "./dashboard.css";
import { Button, Layout, Spin } from "antd";
import {
  ArrowRightOutlined,
  DollarCircleOutlined,
  CarryOutOutlined,
  UserOutlined,
  PhoneOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  BankOutlined,
} from "@ant-design/icons";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import { displayMessage, getAPI } from "../../utils/common";
import { ERROR_MSG_TYPE } from "../../constants/messages";
import { DASH_DATA } from "../../constants/api";
import { Doughnut, Bar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import {
  CURRENCY,
  HOTEL_ID_SERIES,
  isAdmin,
  isSuperAdmin,
  isVendor,
} from "../../constants/constent";
import { VENDOR_BY_ID } from "../../constants/api";
import { postAPI } from "../../utils/common";
import LS_SERVICE from "../../utils/localStorage";
import { useNavigate } from "react-router-dom";
import assets from "../../utils/ImagesPath";
import VendorDetailCard from "../vendor/blocs/VendorDetailCard";
import VendorIdCard from "../vendor/blocs/VendorIdCard";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
);

const Dashboard = () => {
  const { Content } = Layout;
  const [selectedWeek, setSelectedWeek] = useState("this_month");
  const navigate = useNavigate();

  // For Greeting
  let greeting;
  const date = new Date();
  const currentTime = date.getHours();
  if (currentTime >= 0 && currentTime <= 12) {
    greeting = "Good Morning";
  } else if (currentTime > 12 && currentTime <= 18) {
    greeting = "Good Afternoon";
  } else {
    greeting = "Good Evening";
  }

  const handleWeekChange = (event) => {
    setSelectedWeek(event.target.value);
  };

  // eslint-disable-next-line no-unused-vars
  const statusMap = {
    2: "s-confirmed",
    0: "s-pending",
    3: "s-cancelled",
  };
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dashData, setDashData] = useState({});
  const [vendorDetail, setVendorDetail] = useState(null);

  useLayoutEffect(() => {
    getDashData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWeek]);

  const handleRefresh = () => {
    setRefreshing(true);
    try {
      getDashData(); // Replace with your data fetching function
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setRefreshing(false);
    }
  };
  const getDashData = () => {
    if (isSuperAdmin() || isAdmin()) {
      setLoading(true);
      const successFn = function (data) {
        setLoading(false);
        if (data.status) {
          setDashData(data.data);
          setLoading(false);
        } else {
          setLoading(false);
        }
      };
      const errorFn = function () {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Unable to get Dashboard Data");
      };
      getAPI(`${DASH_DATA}${selectedWeek}`, successFn, errorFn);
    } else if (isVendor()) {
      // Vendors see their own profile + ID card instead of KPI dashboards.
      const vendorId = LS_SERVICE.get("vendor_id");
      if (!vendorId) return;
      setLoading(true);
      postAPI(
        VENDOR_BY_ID,
        { id: vendorId },
        (res) => {
          if (res.status) setVendorDetail(res.data);
          setLoading(false);
        },
        () => setLoading(false),
      );
    }
  };

  const {
    gridData,
    recentBills,
    revenueSnapshot,
    revenueChartData,
    yearlyBills,
  } = dashData;

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <main className="main-content">
          {/* Banner */}
          <section className="banner">
            <div className="banner-text">
              <h1>
                {greeting}, {LS_SERVICE.get("name")}!
              </h1>
              {isVendor() && (
                <>
                  <strong>HOTEL ID:</strong>{" "}
                  {HOTEL_ID_SERIES + LS_SERVICE.get("hotel_id")}
                  <br />
                  <br />
                </>
              )}
              <p>
                Every effort counts and you're making it happen.
                <br />
                Keep up the momentum—you're doing great!
              </p>
              {(isSuperAdmin() || isAdmin()) && (
                <>
                  <Button
                    disabled={loading || refreshing}
                    style={{ marginRight: "5px" }}
                    onClick={handleRefresh}
                    loading={loading || refreshing}
                  >
                    <ReloadOutlined />
                  </Button>
                  <Button
                    onClick={() => {
                      navigate("/vendor/bills");
                    }}
                  >
                    All Bills <ArrowRightOutlined />
                  </Button>
                </>
              )}
            </div>
            <img
              alt={assets.fevicon}
              src={assets.fevicon}
              style={{
                height: "100px",
                opacity: 0.8,
                transform: "rotate(-15deg)",
              }}
            />
          </section>

          {/* ================= VENDOR VIEW ================= */}
          {isVendor() && (
            <Spin spinning={loading}>
              {vendorDetail && (
                <section
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 20,
                    marginTop: 20,
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ flex: "1 1 420px", minWidth: 320 }}>
                    <VendorDetailCard vendor={vendorDetail} />
                  </div>
                  <div style={{ flex: "0 0 auto" }}>
                    <VendorIdCard vendor={vendorDetail} />
                  </div>
                </section>
              )}
            </Spin>
          )}

          {(isSuperAdmin() || isAdmin()) && (
            <Spin spinning={loading || refreshing}>
              {/* KPI Grid */}
              <section className="kpi-grid">
                <Kpi
                  title="Total Revenue"
                  value={CURRENCY + gridData?.totalRevenue}
                  icon={<DollarCircleOutlined />}
                  color="bg-blue"
                />
                <Kpi
                  title="Total Active Vendor"
                  value={gridData?.activeVendorCount}
                  icon={<BankOutlined />}
                  color="bg-green"
                />
                <Kpi
                  title="Total Active BDE"
                  value={gridData?.activeBdeCount}
                  icon={<UserOutlined />}
                  color="bg-blue"
                />
                <Kpi
                  title="Total Leads"
                  value={gridData?.totalLeads}
                  icon={<PhoneOutlined />}
                  color="bg-green"
                />
              </section>
              <section className="kpi-grid">
                <Kpi
                  title="Today Setup Revenue"
                  value={CURRENCY + gridData?.setupRevenue}
                  icon={<CarryOutOutlined />}
                  color="bg-blue"
                />
                <Kpi
                  title="Today Subscription Revenue"
                  value={CURRENCY + gridData?.subscriptionRevenue}
                  icon={<ShoppingCartOutlined />}
                  color="bg-blue"
                />
                <Kpi
                  title="Today Setup Bills"
                  value={gridData?.todaySetupBills}
                  icon={<CheckCircleOutlined />}
                  color="bg-orange"
                />
                <Kpi
                  title="Today Subscription Bills"
                  value={gridData?.todaySubscriptionBills}
                  icon={<ReloadOutlined />}
                  color="bg-pink"
                />
              </section>
              {/* Recent Bills */}
              <section className="content-grid">
                <div className="left-col">
                  <div className="card">
                    <div className="card-header">
                      <h3>Recent Bills</h3>
                      <Button
                        style={{
                          backgroundColor: "white",
                          color: "black",
                          border: "none",
                        }}
                        onClick={() => {
                          navigate("/vendor/bills");
                        }}
                      >
                        View All
                      </Button>
                    </div>
                    <BookingTable recentBills={recentBills} />
                  </div>

                  <YearlyBookings yearlyBills={yearlyBills} />
                </div>

                <div className="right-col">
                  <RevenueSnapshot revenueSnapshot={revenueSnapshot} />
                  <Revenue
                    selectedWeek={selectedWeek}
                    handleWeekChange={handleWeekChange}
                    revenueChartData={revenueChartData}
                  />
                </div>
              </section>
            </Spin>
          )}
        </main>
      </Content>
      <LayoutFooter />
    </Layout>
  );
};

const Kpi = ({ title, value, icon, color }) => (
  <div className="kpi-card">
    <div className="kpi-info">
      <p>{title}</p>
      <h2>{value}</h2>
    </div>
    <div className={`kpi-icon ${color}`}>{icon}</div>
  </div>
);

const BookingTable = ({ recentBills }) => {
  const renderBookingRow = (bill) => {
    return (
      <BookingRow
        key={bill.id}
        name={bill.vendorName}
        billType={bill.billType}
        amount={CURRENCY + bill.amount}
        bill_gen_date={bill.billGenDate}
        billing_start_date={bill.billStartDate}
        billing_end_date={bill.billEndDate}
        status={bill.billStatus}
      />
    );
  };

  return (
    <table>
      <thead>
        <tr>
          <th>Hotel Name</th>
          <th>Bill Type</th>
          <th>Gen Date</th>
          <th>Start Date</th>
          <th>End Date</th>
          <th>Amount</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>{recentBills?.map(renderBookingRow)}</tbody>
    </table>
  );
};

const BookingRow = ({
  name,
  billType,
  amount,
  bill_gen_date,
  billing_start_date,
  billing_end_date,
  status,
}) => (
  <tr style={{ textAlign: "left" }}>
    <td>
      <UserOutlined /> {name}
    </td>
    <td>{billType}</td>
    <td>{bill_gen_date}</td>
    <td>{billing_start_date}</td>
    <td>{billing_end_date}</td>
    <td>{amount}</td>
    <td>
      <span
        className={`status ${
          status === "Paid"
            ? "s-confirmed"
            : status === "Pending"
              ? "s-pending"
              : "s-cancelled"
        }`}
      >
        {status}
      </span>
    </td>
  </tr>
);

const YearlyBookings = ({ yearlyBills }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>This Year Bill</h3>
      </div>

      <div
        className="chart-container"
        style={{ width: "auto", height: "100%" }}
      >
        {yearlyBills ? (
          <Bar
            data={yearlyBills}
            options={{
              responsive: true,
              scales: {
                x: {
                  beginAtZero: true,
                },
              },
            }}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

const RevenueSnapshot = ({ revenueSnapshot }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Today Revenue Snapshot</h3>
        <i
          className="fa-solid fa-file-invoice-dollar"
          style={{ color: "var(--grey)" }}
        ></i>
      </div>
      <div className="rev-list">
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-solid fa-money-bill-wave"></i> Cash
          </span>
          <span className="rev-value">{CURRENCY + revenueSnapshot?.cash}</span>
        </div>
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-regular fa-credit-card"></i> Card / Online /
            Prepaid
          </span>
          <span className="rev-value">
            {CURRENCY + revenueSnapshot?.online}
          </span>
        </div>
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-solid fa-clock-rotate-left"></i> Pending
          </span>
          <span className="rev-value" style={{ color: "var(--warning)" }}>
            {CURRENCY + revenueSnapshot?.pendingAmount}
          </span>
        </div>
        <div className="rev-item">
          <span className="rev-label">
            <i className="fa-solid fa-percent"></i> GST
          </span>
          <span className="rev-value">{CURRENCY + revenueSnapshot?.gst}</span>
        </div>
      </div>
    </div>
  );
};

const Revenue = ({ selectedWeek, handleWeekChange, revenueChartData }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h3>{selectedWeek === "this_month" ? "Montly" : "Yearly"} Revenue</h3>
        <select
          style={{
            border: "none",
            color: "var(--grey)",
            outline: "none",
            fontSize: "13px",
          }}
          value={selectedWeek}
          onChange={handleWeekChange}
        >
          <option value="this_month">This Month</option>
          <option value="this_year">This Year</option>
        </select>
      </div>
      <div className="donut-wrapper" style={{ width: "auto", height: "350px" }}>
        {revenueChartData ? (
          <Doughnut
            data={revenueChartData ?? null}
            options={{
              responsive: true,
              maintainAspectRatio: false, // Prevents maintaining aspect ratio for custom size

              plugins: {
                legend: {
                  position: "top",
                },
                tooltip: {
                  callbacks: {
                    label: function (context) {
                      return "₹" + context.raw;
                    },
                  },
                },
              },
            }}
          />
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
